'use client'

import React, {
  type ElementType,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'

export interface MenuItemProps {
  children: ReactNode
  className?: string
  tabIndex?: number
  onClick?: (event: MouseEvent<HTMLLIElement>) => void
  autoFocus?: boolean
  dense?: boolean
  disabled?: boolean
  disableGutters?: boolean
  divider?: boolean
  selected?: boolean
  component?: ElementType
  [key: string]: unknown
}

export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  className = '',
  tabIndex,
  onClick,
  autoFocus = false,
  dense = false,
  disabled = false,
  disableGutters = false,
  divider = false,
  selected = false,
  component: Component = 'li',
  ...props
}) => {
  const menuItemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (autoFocus && menuItemRef.current) {
      menuItemRef.current.focus()
    }
  }, [autoFocus])

  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    if (disabled) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (disabled) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick(event as unknown as MouseEvent<HTMLLIElement>)
    }
  }

  const baseClasses = `
    relative flex items-center justify-start min-h-[48px] cursor-pointer
    transition-colors duration-150 ease-in-out outline-hidden
    focus:bg-gray-100 focus-visible:bg-gray-100
    ${dense ? 'min-h-[32px] py-1' : ''}
    ${disableGutters ? 'px-0' : ''}
    ${divider ? 'border-b border-gray-200' : ''}
    ${selected ? 'bg-gray-50' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
  `
    .trim()
    .replace(/\s+/g, ' ')

  const combinedClassName = `${baseClasses} ${className}`

  return (
    <Component
      ref={menuItemRef}
      className={combinedClassName}
      role="menuitem"
      tabIndex={disabled ? -1 : tabIndex ?? 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </Component>
  )
}

export default MenuItem
